import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { catchError, combineLatest, first, map, of, switchMap } from 'rxjs';
import { CheckoutObject } from '../../../../models/checkout';
import {
  PunchoutAuditMetadata,
  PunchoutCustomerConfig,
  PunchoutError,
  PunchoutOrderRow,
  PunchoutPayload,
  PunchoutProtocol
} from '../../../../models/punchout';
import { ProductService } from '../../../../services/products/product.service';
import { PriceService } from '../../../../services/price/price.service';
import { NavbarCartService } from '../../../../services/navbar-cart/navbar-cart.service';
import { PunchoutService } from '../../../../services/punchout/punchout.service';
import { PUNCHOUT_HANDLER } from '../../../../services/punchout/punchout.tokens';
import { PunchoutProtocolHandler } from '../../../../services/punchout/handlers/punchout-protocol.handler';
import { RuntimeProtocolDelegate } from '../../../../services/punchout/handlers/runtime-protocol-delegate';
import { PunchoutCurrentConfigService } from '../../../../services/punchout/punchout-current-config.service';

@Component({
  selector: 'lib-punchout-form',
  templateUrl: './punchout-form.component.html',
  styleUrls: ['./punchout-form.component.scss'],
  standalone: false
})
export class PunchoutFormComponent implements OnInit {
  protected punchoutService = inject(PunchoutService);
  protected priceService = inject(PriceService);
  protected productService = inject(ProductService);
  protected navbarCartService = inject(NavbarCartService);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected handler = inject<PunchoutProtocolHandler>(PUNCHOUT_HANDLER);
  protected currentConfigService = inject(PunchoutCurrentConfigService);

  @Input() checkoutObject?: CheckoutObject;
  @Output() ready = new EventEmitter<void>();
  @Output() failed = new EventEmitter<unknown>();

  @ViewChild('punchoutForm') punchoutForm?: ElementRef<HTMLFormElement>;

  hookUrl = '';
  target = '_top';

  payload?: PunchoutPayload;
  fieldEntries: { name: string; value: string }[] = [];
  submissionError = false;
  submissionFailureCode?: string;
  started = false;

  ngOnInit(): void {
    this.hookUrl = this.punchoutService.getHookUrl();
    this.target = this.punchoutService.getTarget() || '_top';

    if (!this.hookUrl) {
      this.handleError('Punchout hookUrl is missing.');
      return;
    }
    if (!this.checkoutObject) {
      this.handleError('Punchout checkout data is missing.');
      return;
    }

    const checkoutObject = this.checkoutObject;
    const ready$ =
      this.handler instanceof RuntimeProtocolDelegate ? this.handler.resolve() : of(this.handler);

    ready$
      .pipe(
        switchMap(handler => handler.prepare().pipe(map(() => handler))),
        switchMap(handler =>
          this.currentConfigService.getCurrentConfig().pipe(
            first(),
            switchMap(currentConfig =>
              this.buildRows(checkoutObject).pipe(
                map(rows => ({ handler, currentConfig, rows }))
              )
            )
          )
        ),
        first(),
        catchError(error => {
          this.handleError(error);
          return of(null);
        })
      )
      .subscribe(result => {
        if (!result) {
          return;
        }
        this.handleBuildResult(checkoutObject, result.rows, result.handler, result.currentConfig);
      });
  }

  submit(): boolean {
    const form = this.punchoutForm?.nativeElement;
    if (!this.payload || !form || !this.hookUrl) {
      return false;
    }
    this.started = true;
    this.changeDetectorRef.detectChanges();
    form.submit();
    return true;
  }

  protected buildRows(checkoutObject: CheckoutObject) {
    const checkoutRows = checkoutObject?.rows ?? [];
    if (checkoutRows.length === 0) {
      return of([] as PunchoutOrderRow[]);
    }
    const productIds = checkoutRows.map(r => r.id);
    return combineLatest([
      this.productService.getProductsByIdsUnfiltered(productIds),
      this.navbarCartService.getContents()
    ]).pipe(
      switchMap(([products, navbarCart]) =>
        combineLatest(
          checkoutRows.map(checkoutRow => {
            const product = products.find(p => p.id === checkoutRow.id);
            if (!product) {
              return of(null);
            }
            return this.priceService.getCurrentCustomerPrice(product.id).pipe(
              map(customerPrice => {
                const row: PunchoutOrderRow = {
                  productId: product.id,
                  product,
                  quantity: checkoutRow.quantity ?? 1,
                  price: parseFloat(customerPrice.customerNetPrice.value.toFixed(6)),
                  currency: customerPrice.customerNetPrice.currencyCode || navbarCart?.currency || '',
                  customerPrice
                };
                return row;
              })
            );
          })
        )
      ),
      map(rows => rows.filter((r): r is PunchoutOrderRow => !!r))
    );
  }

  protected handleBuildResult(
    checkoutObject: CheckoutObject,
    rows: PunchoutOrderRow[],
    handler: PunchoutProtocolHandler,
    currentConfig: PunchoutCustomerConfig | null
  ): void {
    const original = { checkoutObject };
    const session = this.punchoutService.getSessionContext();
    const configProtocol = this.getConfigProtocol(currentConfig);
    const baseMetadata = this.buildAuditMetadata(undefined, rows);

    if (session.launchProtocol !== configProtocol) {
      this.handleReturnFailure(
        new PunchoutError(
          'PUNCHOUT_PROTOCOL_MISMATCH',
          `Punchout launch protocol ${session.launchProtocol} does not match backend config format ${configProtocol}.`
        ),
        original,
        currentConfig,
        baseMetadata
      );
      return;
    }

    if (handler.protocol !== configProtocol) {
      this.handleReturnFailure(
        new PunchoutError(
          'PUNCHOUT_HANDLER_CONFIG_MISMATCH',
          `Punchout handler protocol ${handler.protocol} does not match backend config format ${configProtocol}.`
        ),
        original,
        currentConfig,
        baseMetadata
      );
      return;
    }

    try {
      const payload = handler.buildPayload(rows, { session, currentConfig, checkoutObject });
      this.punchoutService
        .persistPunchoutSubmission(
          original,
          payload,
          {
            currentConfig,
            metadata: this.buildAuditMetadata(payload, rows)
          }
        )
        .pipe(first())
        .subscribe({
          next: () => {
            this.payload = payload;
            this.fieldEntries = handler.getFormFields(payload);
            this.changeDetectorRef.detectChanges();
            this.ready.emit();
          },
          error: error => this.handleError(error)
        });
    } catch (error) {
      this.handleReturnFailure(error, original, currentConfig, baseMetadata);
    }
  }

  protected handleReturnFailure(
    error: unknown,
    original: { checkoutObject: CheckoutObject },
    currentConfig: PunchoutCustomerConfig | null,
    metadata: PunchoutAuditMetadata
  ): void {
    const failureCode = this.getFailureCode(error);
    this.punchoutService
      .persistPunchoutFailure(original, failureCode, {
        currentConfig,
        metadata: {
          ...metadata,
          failureCode
        }
      })
      .pipe(first())
      .subscribe({
        next: () => this.handleError(error, failureCode),
        error: auditError => this.handleError({ error, auditError }, failureCode)
      });
  }

  protected handleError(error: unknown, failureCode = this.getFailureCode(error)): void {
    this.submissionError = true;
    this.submissionFailureCode = failureCode;
    this.failed.emit(error);
    console.error('PunchoutFormComponent: failed to prepare punchout form.', error);
    this.changeDetectorRef.detectChanges();
  }

  protected getConfigProtocol(currentConfig: PunchoutCustomerConfig | null): PunchoutProtocol {
    return currentConfig?.format ?? 'oci';
  }

  protected buildAuditMetadata(
    payload: PunchoutPayload | undefined,
    rows: PunchoutOrderRow[],
    failureCode?: string
  ): PunchoutAuditMetadata {
    const currencies = Array.from(new Set(rows.map(row => row.currency).filter(Boolean)));
    return {
      payloadKind: payload?.kind,
      rowCount: rows.length,
      total: rows.reduce((sum, row) => sum + (row.quantity ?? 1) * row.price, 0),
      currency: currencies.length === 1 ? currencies[0] : currencies.join(','),
      failureCode
    };
  }

  protected getFailureCode(error: unknown): string {
    if (error instanceof PunchoutError) {
      return error.code;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'PUNCHOUT_SUBMIT_FAILED';
  }
}
